/**
 * consignorResolve.ts — resolve an import file's `Consignor` column to consignors/{uid}.
 *
 * WHY (TD-98, 2026-09-15). Lots used to carry a free-text `consignor` CODE (DS / AL / JR) and
 * consignorId was stamped LATER by one-off scripts holding a hardcoded code→uid table
 * (seed-consignors.mjs). JR was Joe Renner and also Jason Roers's initials. A code table is
 * only as unique as the people who typed it, and it lives in a script, not in the data.
 *
 * THE RULE NOW: the column carries a CONSIGNOR NUMBER — AuctionFlex's B####, or HOUSE — the
 * same number AuctionFlex/HiBid exports already carry (process-march-2026-closedown.js read
 * B6757 from one). It resolves at IMPORT, against the live consignor records, to exactly one
 * consignors/{uid}. Zero matches or more than one REFUSES the whole file — one person with two
 * records (TD-97) surfaces here as "matches 2 records", which is the point.
 *
 * A record IS number X when `consignorNumber == X`, or when its doc id is X (the older B####-
 * keyed records). A record carrying `mergedInto` resolves to that target instead.
 * Pure — no Firebase — so it can be tested without a project.
 */

export interface ConsignorRecordLite {
  id: string;                      // consignors/{id}
  consignorNumber?: string | null;
  legacyCode?: string | null;      // old initials code — recognised only to explain a refusal
  name?: string | null;
  /**
   * Set by merge-joe-renner-records.js (and any later merge): this record was folded into
   * consignors/{mergedInto}. It is an ALIAS, not a second person — it resolves to its target
   * and never counts as a competing match.
   */
  mergedInto?: string | null;
}

export interface ConsignorResolution {
  ok: boolean;
  /** consignor number → consignors doc id */
  byNumber: Record<string, string>;
  errors: string[];
  lotsExamined: number;
  lotsAttributed: number;
  /** Lots with an empty Consignor cell. Imported uncredited — reported, not refused. */
  lotsBlank: number;
  recordsExamined: number;
}

const NUMBER_RE = /^(B\d{3,}|HOUSE)$/;
const norm = (v: unknown) => String(v ?? '').trim().toUpperCase();

export function resolveConsignors(
  lots: { consignor?: string | null; sku?: string }[],
  records: ConsignorRecordLite[],
): ConsignorResolution {
  const errors: string[] = [];
  const byNumber: Record<string, string> = {};
  const values = new Map<string, number>();   // number -> lot count
  let blank = 0;
  for (const l of lots) {
    const v = norm(l.consignor);
    if (!v) { blank++; continue; }
    values.set(v, (values.get(v) || 0) + 1);
  }

  if (values.size > 0 && records.length === 0) {
    errors.push('No consignor records were readable, so no Consignor value could be resolved. ' +
      'Importing a consignor-attributed file needs staff (admin/auctioneer) access.');
  }

  for (const [v, count] of values) {
    if (!NUMBER_RE.test(v)) {
      const legacy = records.filter(r => norm(r.legacyCode) === v);
      errors.push(`Consignor "${v}" on ${count} lot(s) is not a consignor number (B#### or HOUSE).` +
        (legacy.length ? ` It is a legacy initials code (${legacy.map(r => r.name || r.id).join(', ')}) — re-export with the consignor number.` : ''));
      continue;
    }
    if (records.length === 0) continue;
    const hits = records.filter(r => norm(r.consignorNumber) === v || norm(r.id) === v);
    const byId = new Map(records.map(r => [r.id, r]));
    const unmergedTarget = (r: ConsignorRecordLite): string | null => {
      let cur: ConsignorRecordLite | undefined = r;
      for (let hop = 0; cur && cur.mergedInto && hop < 5; hop++) {
        const next = byId.get(cur.mergedInto);
        if (!next) return null;               // merged into a record that does not exist
        cur = next;
      }
      return cur && !cur.mergedInto ? cur.id : null;
    };
    const broken = hits.filter(r => r.mergedInto && !unmergedTarget(r));
    if (broken.length) {
      errors.push(`Consignor ${v}: record(s) ${broken.map(r => r.id).join(', ')} are marked merged into a record that does not exist.`);
      continue;
    }
    const ids = [...new Set(hits.map(r => unmergedTarget(r) as string))];
    if (ids.length === 0) {
      errors.push(`Consignor ${v} on ${count} lot(s) matches no consignor record. Create or number the record first.`);
    } else if (ids.length > 1) {
      errors.push(`Consignor ${v} on ${count} lot(s) matches ${ids.length} records (${ids.join(', ')}). ` +
        'One person with more than one record — merge them before importing (TD-97).');
    } else {
      byNumber[v] = ids[0];
    }
  }

  const attributed = lots.filter(l => byNumber[norm(l.consignor)]).length;
  return {
    ok: errors.length === 0,
    byNumber,
    errors,
    lotsExamined: lots.length,
    lotsAttributed: attributed,
    lotsBlank: blank,
    recordsExamined: records.length,
  };
}
